import React from 'react';
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  Paper,
  Stack,
  Tooltip,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import { borderRadius, getControlBorderColor, getGlassCard, layoutGap } from '../../../../theme/theme';
import { WorkspaceMetricGrid } from '../../../../shared/ui/workflow/WorkflowWorkspacePrimitives';
import { MappingMetricCard } from '../../../../shared/ui/mapping';
import HelpTooltipTrigger from '../../../../shared/ui/common/HelpTooltipTrigger';
import WorkspaceIntroCard, {
  type WorkspaceIntroCardProps,
} from '../../../../shared/ui/common/WorkspaceIntroCard';
import {
  BodyText,
  CaptionText,
  OverlineText,
  SubsectionTitle,
} from '../../../../shared/ui/typography';
import { mappingOverviewTooltips } from '../../../../shared/config/mappingHelpCatalog';
import type { SummaryTone, WorkflowSummaryCard } from '../../../../shared/types/mappingWorkspace.types';

export interface WorkflowGuideCard {
  id: number;
  title: string;
  summary: string;
  hint: string;
  icon: React.ReactNode;
  status?: string | null;
  recommended?: boolean;
}

interface WorkflowCompletionSummaryProps {
  eyebrow?: string;
  title: string;
  summary: string;
  chips: readonly string[];
  cards: readonly WorkflowSummaryCard[];
  guides: readonly WorkflowGuideCard[];
  activeGuideId: number;
  onSelectGuide: (id: number) => void;
  actionLabel?: string;
  onAction?: () => void;
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
  compact?: boolean;
}

export type WorkspaceModeIntroProps = WorkspaceIntroCardProps;

export const WorkspaceModeIntro: React.FC<WorkspaceModeIntroProps> = (props) => (
  <WorkspaceIntroCard {...props} />
);

const WorkflowCompletionSummary: React.FC<WorkflowCompletionSummaryProps> = ({
  eyebrow = 'Setup summary',
  title,
  summary,
  chips,
  cards,
  guides,
  activeGuideId,
  onSelectGuide,
  actionLabel,
  onAction,
  secondaryActionLabel,
  onSecondaryAction,
  compact = false,
}) => {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        ...getGlassCard(theme),
        mx: 0.4,
        mb: 0.35,
        px: { xs: 1, md: 1.25 },
        py: 1,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Stack spacing={layoutGap.row}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={layoutGap.row}
          alignItems={{ xs: 'flex-start', md: 'flex-start' }}
          justifyContent="space-between"
        >
          <Box
            sx={{
              minWidth: 0,
              display: 'inline-block',
              maxWidth: compact ? 560 : 920,
            }}
          >
            <Stack direction="row" spacing={layoutGap.comfort} alignItems="center">
              <OverlineText sx={{ color: alpha(theme.palette.primary.light, 0.88) }}>
                {eyebrow}
              </OverlineText>
              <HelpTooltipTrigger
                label={eyebrow}
                help={mappingOverviewTooltips.workflowCompletion}
                placement="bottom-start"
              />
            </Stack>
            <SubsectionTitle sx={{ mt: 0.35 }}>
              {title}
            </SubsectionTitle>
            <BodyText
              secondary
              sx={{ mt: 0.35, color: alpha(theme.palette.text.secondary, 0.88), maxWidth: 920 }}
            >
              {summary}
            </BodyText>
          </Box>
          {(secondaryActionLabel && onSecondaryAction) || (actionLabel && onAction) ? (
            <Stack direction="row" spacing={layoutGap.blend} flexWrap="wrap" useFlexGap>
              {secondaryActionLabel && onSecondaryAction && (
                <Button
                  size="small"
                  variant="text"
                  onClick={onSecondaryAction}
                  sx={{
                    color: theme.palette.text.secondary,
                  }}
                >
                  {secondaryActionLabel}
                </Button>
              )}
              {actionLabel && onAction && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={onAction}
                  sx={{
                    whiteSpace: 'nowrap',
                    borderRadius: `${borderRadius.md}px`,
                    borderColor: getControlBorderColor(theme),
                    backgroundColor: alpha(theme.palette.background.default, 0.1),
                    '&:hover': {
                      borderColor: getControlBorderColor(theme),
                    },
                  }}
                >
                  {actionLabel}
                </Button>
              )}
            </Stack>
          ) : null}
        </Stack>

        {!compact && (
          <>
            <Stack direction="row" spacing={layoutGap.loft} useFlexGap flexWrap="wrap">
              {chips.map((chip) => (
                    <Chip
                      key={chip}
                      size="small"
                      label={chip}
                      sx={{
                        height: 22,
                        borderRadius: `${borderRadius.full}px`,
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        backgroundColor: alpha(theme.palette.background.default, 0.18),
                        border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                      }}
                    />
                  ))}
            </Stack>

            <WorkspaceMetricGrid
              sx={{
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  xl: `repeat(${Math.min(cards.length, 5)}, minmax(0, 1fr))`,
                },
              }}
            >
              {cards.map((card) => (
                <MappingMetricCard
                  key={card.label}
                  label={card.label}
                  value={card.value}
                  supporting={card.detail}
                  tone={card.tone === 'default' ? 'neutral' : card.tone}
                />
              ))}
            </WorkspaceMetricGrid>

            <Box>
              <Stack direction="row" spacing={layoutGap.comfort} alignItems="center">
                <OverlineText sx={{ color: alpha(theme.palette.text.secondary, 0.82) }}>
                  Workspace guide
                </OverlineText>
                <HelpTooltipTrigger
                  label="Workspace guide"
                  help={mappingOverviewTooltips.workspaceGuide}
                  placement="bottom-start"
                />
              </Stack>
              <Box
                sx={{
                  mt: 0.65,
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                  gap: layoutGap.row,
                }}
              >
                {guides.map((guide) => {
                  const active = guide.id === activeGuideId;

                  return (
                    <ButtonBase
                      key={guide.id}
                      onClick={() => onSelectGuide(guide.id)}
                      aria-label={`Open ${guide.title}`}
                      sx={{
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        borderRadius: `${borderRadius.md}px`,
                      }}
                    >
                      <Box
                        sx={{
                          width: '100%',
                          minHeight: 102,
                          px: 1,
                          py: 0.95,
                          borderRadius: `${borderRadius.md}px`,
                          border: `1px solid ${getControlBorderColor(theme)}`,
                          backgroundColor: active
                            ? alpha(theme.palette.primary.main, 0.08)
                            : alpha(theme.palette.background.default, 0.08),
                          transition: 'transform 0.18s ease, border-color 0.18s ease, background-color 0.18s ease',
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            borderColor: getControlBorderColor(theme),
                            backgroundColor: alpha(theme.palette.primary.main, active ? 0.15 : 0.08),
                          },
                        }}
                      >
                        <Stack direction="row" spacing={layoutGap.comfort} alignItems="flex-start" justifyContent="space-between">
                          <Stack direction="row" spacing={layoutGap.comfort} alignItems="flex-start" sx={{ minWidth: 0 }}>
                            <Box
                              sx={{
                                width: 28,
                                height: 28,
                                borderRadius: `${borderRadius.md}px`,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: active ? theme.palette.primary.light : alpha(theme.palette.text.secondary, 0.9),
                                backgroundColor: alpha(theme.palette.background.default, active ? 0.28 : 0.18),
                                flexShrink: 0,
                                '& .MuiSvgIcon-root': {
                                  fontSize: '1rem !important',
                                },
                              }}
                            >
                              {guide.icon}
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                              <BodyText secondary>{guide.title}</BodyText>
                              <CaptionText
                                sx={{
                                  display: 'block',
                                  mt: 0.35,
                                  color: alpha(theme.palette.text.secondary, 0.84),
                                }}
                              >
                                {guide.summary}
                              </CaptionText>
                            </Box>
                          </Stack>
                          {guide.status && (
                            <Stack spacing={layoutGap.snug} alignItems="flex-end" sx={{ flexShrink: 0 }}>
                              <Tooltip
                                title={guide.recommended ? 'Recommended next step' : ''}
                                arrow
                                disableHoverListener={!guide.recommended}
                              >
                                <Chip
                                  size="small"
                                  label={guide.status}
                                  sx={{
                                    height: 18,
                                    borderRadius: `${borderRadius.full}px`,
                                    fontSize: '0.64rem',
                                    fontWeight: 700,
                                    backgroundColor: guide.recommended
                                      ? alpha(theme.palette.success.main, 0.14)
                                      : alpha(theme.palette.primary.main, active ? 0.18 : 0.1),
                                    border: guide.recommended
                                      ? `1px solid ${alpha(theme.palette.success.main, 0.2)}`
                                      : `1px solid ${alpha(theme.palette.primary.main, active ? 0.22 : 0.14)}`,
                                    color: guide.recommended ? theme.palette.success.light : undefined,
                                  }}
                                />
                              </Tooltip>
                            </Stack>
                          )}
                          {!guide.status && guide.recommended && (
                            <Chip
                              size="small"
                              label="Recommended next step"
                              sx={{
                                height: 18,
                                borderRadius: `${borderRadius.full}px`,
                                fontSize: '0.62rem',
                                fontWeight: 700,
                                backgroundColor: alpha(theme.palette.success.main, 0.14),
                                border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                                color: theme.palette.success.light,
                                flexShrink: 0,
                              }}
                            />
                          )}
                        </Stack>
                        <Stack
                          direction="row"
                          spacing={layoutGap.comfort}
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{ mt: 0.8 }}
                        >
                          <CaptionText sx={{ color: alpha(theme.palette.text.primary, 0.94), pr: 1 }}>
                            {guide.hint}
                          </CaptionText>
                          <Stack
                            direction="row"
                            spacing={layoutGap.micro}
                            alignItems="center"
                            sx={{
                              color: active ? theme.palette.primary.light : alpha(theme.palette.primary.light, 0.88),
                              flexShrink: 0,
                            }}
                          >
                            <CaptionText sx={{ whiteSpace: 'nowrap' }}>
                              {active ? 'Selected' : 'Open workspace'}
                            </CaptionText>
                            <ArrowOutwardIcon sx={{ fontSize: '0.9rem' }} />
                          </Stack>
                        </Stack>
                      </Box>
                    </ButtonBase>
                  );
                })}
              </Box>
            </Box>
          </>
        )}
      </Stack>
    </Paper>
  );
};

export default WorkflowCompletionSummary;
